'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { Gift, Zap } from 'lucide-react';
import { Switch } from '@/components/ui/switch/switch';
import { cn } from '@/lib/utils';
import type { SprintDefinition } from '../sprints';

interface SprintToggleBarProps {
  sprints: SprintDefinition[];
  selectedSprintId: string;
  onSelectSprint: (id: string) => void;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}

type CountdownPhase = 'upcoming' | 'live' | 'ended';

interface Countdown {
  phase: CountdownPhase;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function computeCountdown(sprint: SprintDefinition, now: number): Countdown {
  const startMs = new Date(sprint.startsAt).getTime();
  const endMs = new Date(sprint.endsAt).getTime();
  const phase: CountdownPhase = now < startMs ? 'upcoming' : now > endMs ? 'ended' : 'live';
  const target = phase === 'upcoming' ? startMs : endMs;
  const ms = Math.max(0, target - now);
  return {
    phase,
    days: Math.floor(ms / 86_400_000),
    hours: Math.floor((ms % 86_400_000) / 3_600_000),
    minutes: Math.floor((ms % 3_600_000) / 60_000),
    seconds: Math.floor((ms % 60_000) / 1000),
  };
}

export function SprintToggleBar({
  sprints,
  selectedSprintId,
  onSelectSprint,
  enabled,
  onToggle,
}: SprintToggleBarProps) {
  const selectedSprint = useMemo(
    () => sprints.find((s) => s.id === selectedSprintId) ?? sprints[0],
    [sprints, selectedSprintId],
  );

  const [countdown, setCountdown] = useState<Countdown | null>(null);

  useEffect(() => {
    if (!enabled || !selectedSprint) {
      setCountdown(null);
      return;
    }
    const tick = () => setCountdown(computeCountdown(selectedSprint, Date.now()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [enabled, selectedSprint]);

  const switchId = 'sprint-toggle';
  const showPills = enabled && sprints.length > 1;

  return (
    <div
      className={cn(
        'mt-3 px-3 md:px-4 py-2.5 rounded-xl border transition-colors',
        enabled ? 'bg-dgr-gold/10 border-dgr-gold/40' : 'bg-white/70 border-stone-200',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label htmlFor={switchId} className="flex items-center gap-2 cursor-pointer select-none">
          <Switch id={switchId} checked={enabled} onCheckedChange={onToggle} />
          <span className="flex items-center gap-1.5 text-sm font-medium text-stone-800">
            <Zap
              className={cn(
                'w-4 h-4',
                enabled ? 'text-dgr-gold-hover fill-dgr-gold/40' : 'text-stone-400',
              )}
            />
            {enabled && sprints.length > 1 ? 'Sprints' : (selectedSprint?.label ?? 'Sprints')}
          </span>
        </label>

        {showPills && (
          <div
            role="tablist"
            aria-label="Select sprint"
            className="flex items-center gap-1 flex-wrap"
          >
            {sprints.map((s) => {
              const isActive = s.id === selectedSprintId;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onSelectSprint(s.id)}
                  className={cn(
                    'px-2.5 py-1 rounded-md text-xs font-medium transition-colors border',
                    isActive
                      ? 'bg-dgr-gold/30 text-stone-900 border-dgr-gold/50'
                      : 'bg-white/70 text-stone-600 border-stone-200 hover:bg-white hover:text-stone-800',
                  )}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        )}

        {enabled && countdown && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-stone-500">
              {countdown.phase === 'ended'
                ? 'Sprint ended'
                : countdown.phase === 'upcoming'
                  ? 'Starts in'
                  : 'Ends in'}
            </span>
            {countdown.phase !== 'ended' && (
              <span className="font-mono font-semibold text-stone-900 tabular-nums">
                {countdown.days}d {String(countdown.hours).padStart(2, '0')}h{' '}
                {String(countdown.minutes).padStart(2, '0')}m{' '}
                {String(countdown.seconds).padStart(2, '0')}s
              </span>
            )}
          </div>
        )}

        {enabled && (
          <Link
            href="/rewards"
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-dgr-gold-hover hover:text-dgr-gold-hover/80 transition-colors"
          >
            <Gift className="w-4 h-4" />
            View Rewards
          </Link>
        )}
      </div>

      {enabled && selectedSprint?.prize && (
        <p className="mt-1.5 text-xs text-stone-600">
          <span className="font-medium text-stone-700">{selectedSprint.label}:</span>{' '}
          {selectedSprint.prize}
        </p>
      )}
    </div>
  );
}
